import React, { useState, useEffect } from 'react';
import { ChevronRight, X } from 'lucide-react';
import DatePickerCalendar from './DatePickerCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExitIntent = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Only on desktop, only once per session
    if (dismissed || window.innerWidth < 1024) return;
    if (sessionStorage.getItem('wve_exit_shown')) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !dismissed) {
        setShow(true);
        sessionStorage.setItem('wve_exit_shown', '1');
      }
    };

    // Delay attaching so it doesn't fire immediately
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 15000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [dismissed]);

  const close = () => { setShow(false); setDismissed(true); };

  const checkDate = async () => {
    if (!selectedDate) return;
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/check-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate }),
      });
      const data = await res.json();
      setResult(data);
      if (data.status !== 'unavailable') {
        setTimeout(() => {
          close();
          window.location.href = 'https://www.weddingvideo.co.uk/contact';
        }, 2000);
      }
    } catch {
      setResult({ status: 'error', message: 'Something went wrong.' });
    } finally {
      setChecking(false);
    }
  };

  const statusColors = {
    available: 'bg-emerald-500/20 border-emerald-400 text-emerald-300',
    limited: 'bg-amber-500/20 border-amber-400 text-amber-300',
    unavailable: 'bg-red-500/20 border-red-400 text-red-300',
    error: 'bg-red-500/20 border-red-400 text-red-300',
  };

  if (!show) return null;

  return (
    <div
      data-testid="exit-intent-overlay"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      onClick={close}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        data-testid="exit-intent-popup"
        className="relative rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-up border border-white/10"
        style={{ backgroundColor: '#1A1A1A' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={close} className="absolute top-4 right-4 text-white/40 hover:text-white z-10">
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 text-center">
          <p className="text-gold text-xs font-bold uppercase tracking-widest mb-3">Before you go</p>
          <h3 className="font-serif text-2xl text-white mb-2">Is your date still available?</h3>
          <p className="text-white/50 text-sm mb-6">Dates fill up fast. A quick check takes 2 seconds.</p>

          <div className="flex gap-2">
            <DatePickerCalendar
              value={selectedDate}
              onChange={(d) => { setSelectedDate(d); setResult(null); }}
              className="flex-1"
            />
            <button
              onClick={checkDate}
              disabled={!selectedDate || checking}
              className="bg-gold hover:bg-gold-light text-black font-semibold px-5 py-3 rounded-lg text-sm transition-all disabled:opacity-50 flex items-center gap-1"
            >
              {checking ? (
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>Check <ChevronRight className="w-3 h-3" /></>
              )}
            </button>
          </div>

          {result && (
            <div className={`mt-4 p-3 rounded-lg border text-sm ${statusColors[result.status]} animate-fade-up`}>
              <p className="font-semibold">{result.message}</p>
            </div>
          )}

          <p className="text-white/30 text-xs mt-4">No spam. No commitment. Just a quick check.</p>
        </div>
      </div>
    </div>
  );
};

export default ExitIntent;
